import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faEnvelope, faPhone, faMapMarkerAlt, faIndustry,
  faExclamationTriangle, faSync
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_URL = `${API_CONFIG2.apiUrl}entreprises/api_entreprise_single.php`;

const CardEntreprise = ({ id }) => {
  const [entreprise, setEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchEntreprise = async (forceRefresh = false) => {
    if (!id) {
      setError('Aucun ID fourni');
      setLoading(false);
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const url = `${API_URL}?id=${id}&unique=${new Date().getTime()}`;
      const res = await fetch(url);
      const text = await res.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        // Si la réponse n'est pas du JSON valide
        throw new Error(`Réponse invalide : ${text.substring(0, 80)}`);
      }

      if (!res.ok) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      if (data.status === 'success') {
        setEntreprise(data.data);
        setError(null);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      setError(err.message);
      setEntreprise(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEntreprise();
  }, [id]);

  if (loading) {
    return (
      <div className="card-entreprise-loading">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-secondary">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-entreprise-error">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
        <span className="text-danger">{error}</span>
        <button className="monetra-btn primary mt-2" onClick={() => fetchEntreprise(true)}>
          <FontAwesomeIcon icon={faSync} /> Réessayer
        </button>
      </div>
    );
  }

  if (!entreprise) {
    return (
      <div className="card-entreprise-empty">
        <p className="text-secondary">Aucune donnée disponible.</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .card-entreprise-wrapper {
          background: rgba(20, 40, 80, 0.55);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          border-radius: 24px;
          padding: clamp(1.2rem, 2vw, 2rem);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          transition: all 0.2s ease;
          max-width: 600px;
          margin: 0 auto;
          color: #e0e7ff;
        }
        .card-entreprise-wrapper:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }
        .card-entreprise-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 1.2rem;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          padding-bottom: 1rem;
        }
        .card-entreprise-header .icon-big {
          font-size: 2.2rem;
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.15);
          padding: 12px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .card-entreprise-header .title {
          font-size: clamp(1.3rem, 2vw, 1.8rem);
          font-weight: 700;
          color: #fff;
        }
        .card-entreprise-header .sub {
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          color: #94a3b8;
          margin-top: 2px;
        }
        .card-entreprise-body {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }
        .card-entreprise-body .info-row {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: clamp(0.9rem, 1.1vw, 1rem);
          color: #94a3b8;
          padding: 6px 0;
          border-bottom: 1px solid rgba(255,255,255,0.03);
        }
        .card-entreprise-body .info-row .icon {
          width: 24px;
          color: #64748b;
          font-size: 1rem;
        }
        .card-entreprise-body .info-row .value {
          color: #e0e7ff;
          word-break: break-word;
        }
        .card-entreprise-body .sector-badge {
          display: inline-block;
          margin-top: 6px;
          padding: 6px 18px;
          background: rgba(59, 130, 246, 0.12);
          border-radius: 30px;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
          color: #3b82f6;
          font-weight: 500;
          align-self: flex-start;
        }
        .card-entreprise-loading,
        .card-entreprise-error,
        .card-entreprise-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: rgba(20, 40, 80, 0.55);
          backdrop-filter: blur(16px);
          border-radius: 24px;
          border: 1px solid rgba(59, 130, 246, 0.15);
          min-height: 200px;
          color: #e0e7ff;
        }
        .monetra-btn {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: #e0e7ff;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn.primary {
          background: #3b82f6;
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.25);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: #1d4ed8;
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .text-secondary { color: #94a3b8; }
        .text-danger { color: #ef4444; }
        .mt-2 { margin-top: 0.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .text-center { text-align: center; }
      `}</style>

      <div className="card-entreprise-wrapper">
        <div className="card-entreprise-header">
          <div className="icon-big">
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <div>
            <div className="title">{entreprise.nomEntreprise}</div>
            <div className="sub">ID #{entreprise.id}</div>
          </div>
        </div>

        <div className="card-entreprise-body">
          <div className="info-row">
            <span className="icon"><FontAwesomeIcon icon={faEnvelope} /></span>
            <span className="value">{entreprise.email}</span>
          </div>
          <div className="info-row">
            <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
            <span className="value">{entreprise.telephone}</span>
          </div>
          <div className="info-row">
            <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
            <span className="value">{entreprise.adresse}</span>
          </div>
          <div className="info-row">
            <span className="icon"><FontAwesomeIcon icon={faIndustry} /></span>
            <span className="sector-badge">{entreprise.secteur_activite}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CardEntreprise;